import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { decodeHtmlEntities } from 'src/helper/helperFunction';
import { SKOLA } from 'src/constants/role';
import { getSchoolInfo } from 'src/api/schoolMetod';

/* SKOLA STRANA ZA EDITOVANJE POSTOJECE ZALBE */
const EditComplaintDialog = ({
  open,
  onClose,
  complaintData,
  onSubmit,
  schoolId,
  roleProp
}) => {
  const { t } = useTranslation();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedProfessor, setSelectedProfessor] = useState('');
  const [complaintMessage, setComplaintMessage] = useState('');
  const [error, setError] = useState(null);
  const [school, setSchool] = useState({ name: '' });

  useEffect(() => {
    if (complaintData) {
      setSelectedStudent(complaintData.student || null);
      setSelectedProfessor(complaintData.student?.professor || '');
      setComplaintMessage(decodeHtmlEntities(complaintData.poruka) || '');

      const fetchSchoolData = async () => {
        try {
          if (roleProp === SKOLA) {
            const data = await getSchoolInfo(schoolId);
            setSchool(data || { name: '' });
          }
        } catch (error) {
          setError(t('dialog.loadSchoolError'));
          console.error('Error fetching data:', error);
        }
      };

      fetchSchoolData();
    }
  }, [complaintData, schoolId, roleProp, t]);

  const handleSubmit = async () => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/complaints.php?id=${complaintData.id}`,
        {
          poruka: complaintMessage
        }
      );
      onSubmit();
      onClose();
    } catch (error) {
      setError(t('dialog.saveError'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontSize: '1.25rem',
          fontWeight: 'bold',
          padding: '16px',
          borderBottom: '1px solid #ddd',
          color: '#333',
          textAlign: 'center',
          backgroundColor: '#f7f9fc',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        }}
      >
        {t('dialog.editComplaint')}
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          backgroundColor: '#f7f9fc'
        }}
      >
        <Box
          component="form"
          sx={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            border: '1px solid #ddd'
          }}
          noValidate
          autoComplete="off"
        >
          {error && (
            <Typography color="error" sx={{ marginBottom: '8px' }}>
              {error}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <DateRangeIcon sx={{ color: '#2596be' }} />
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: '#333' }}
            >
              {t('dialog.dateLabel')}: {complaintData.date}
            </Typography>
          </Box>

          <TextField
            label={t('dialog.schoolLabel')}
            value={school.name || ''}
            fullWidth
            variant="outlined"
            size="small"
            sx={{ marginBottom: '16px' }}
            disabled
          />

          <TextField
            label={t('dialog.studentNameLabel')}
            value={selectedStudent ? selectedStudent.name : ''}
            fullWidth
            variant="outlined"
            size="small"
            disabled
          />

          <TextField
            label={t('dialog.classLabel')}
            value={selectedStudent?.class || ''}
            fullWidth
            variant="outlined"
            size="small"
            disabled
          />

          <TextField
            label={t('dialog.professorLabel')}
            value={selectedProfessor || ''}
            fullWidth
            variant="outlined"
            size="small"
            disabled
          />

          <TextField
            label={t('dialog.complaintMessageLabel')}
            value={complaintMessage || ''}
            onChange={(e) => setComplaintMessage(e.target.value)}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px',
          borderTop: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: '#f7f9fc',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px'
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: '#2596be',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#e3f2fd'
            }
          }}
        >
          {t('dialog.cancelButton')}
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#2596be',
            color: '#fff',
            padding: '8px 16px',
            textTransform: 'none',
            fontWeight: 'bold',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#1b7fa7'
            }
          }}
          onClick={handleSubmit}
        >
          {t('dialog.saveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditComplaintDialog;
